import React, { useEffect, useRef, useState } from 'react';
import { Search, Loader2, Store as StoreIcon, ShoppingBag, Tag, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { formatMoney } from '../lib/money';

/**
 * Universal hero search — debounced. Searches vendors + items + categories
 * via /api/search. Suggestions dropdown shows hits as the user types.
 */
const HeroSearch = ({ placeholder = 'Search businesses, products, services…' }) => {
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hits, setHits] = useState({ vendors: [], items: [], categories: [] });
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const term = q.trim();
    if (term.length < 2) { setHits({ vendors: [], items: [], categories: [] }); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/search', { params: { q: term, limit: 6 } });
        setHits({
          vendors: data.vendors || [],
          items: data.items || [],
          categories: data.categories || [],
        });
      } catch {
        setHits({ vendors: [], items: [], categories: [] });
      } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  // Click outside closes dropdown
  useEffect(() => {
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const empty = !loading && hits.vendors.length === 0 && hits.items.length === 0 && hits.categories.length === 0;
  const hasFocus = open && q.trim().length >= 2;

  const handleSubmit = () => {
    const term = q.trim();
    if (!term) return;
    nav(`/?q=${encodeURIComponent(term)}`);
    setOpen(false);
    // Smooth scroll to the vendor list section
    setTimeout(() => {
      const target = document.getElementById('home-vendor-list');
      if (target) target.scrollIntoView({ behavior: 'smooth' });
    }, 80);
  };

  return (
    <div ref={wrapRef} className="relative w-full" data-testid="hero-search-wrap">
      <div className="relative">
        <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); if (e.key === 'Escape') setOpen(false); }}
          placeholder={placeholder}
          data-testid="hero-search-input"
          className="w-full h-11 sm:h-12 pl-10 pr-4 rounded-full bg-white/95 backdrop-blur text-slate-900 placeholder:text-slate-400 border-0 focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-sm"
        />
        {loading && <Loader2 className="h-4 w-4 animate-spin absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" />}
      </div>

      {hasFocus && (
        <div
          className="absolute left-0 right-0 mt-2 z-40 bg-white border border-slate-200 rounded-2xl shadow-xl max-h-[400px] overflow-y-auto text-slate-900"
          data-testid="hero-search-dropdown"
        >
          {empty && (
            <div className="px-4 py-6 text-center text-sm text-slate-500">
              No matches for <b>"{q}"</b>. Press Enter to search anyway.
            </div>
          )}

          {hits.categories.length > 0 && (
            <Section title="Categories" icon={Tag}>
              {hits.categories.map((c) => (
                <Hit key={c.id} data-testid={`hero-hit-cat-${c.id}`}
                     onClick={() => { nav(`/?cat=${encodeURIComponent(c.id)}`); setOpen(false); }}>
                  <div className="h-8 w-8 rounded-full bg-emerald-50 flex items-center justify-center"><Tag className="h-4 w-4 text-emerald-700" /></div>
                  <span className="font-medium">{c.name}</span>
                </Hit>
              ))}
            </Section>
          )}

          {hits.vendors.length > 0 && (
            <Section title="Stores" icon={StoreIcon}>
              {hits.vendors.map((v) => (
                <Hit key={v.id} data-testid={`hero-hit-vendor-${v.id}`}
                     onClick={() => { nav(`/vendor/${v.id}`); setOpen(false); }}>
                  {v.image
                    ? <img src={v.image} alt={v.name} className="h-9 w-9 rounded-lg object-cover" />
                    : <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center"><StoreIcon className="h-4 w-4 text-slate-500" /></div>}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{v.name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{v.cuisine || v.category}{v.city ? ` · ${v.city}` : ''}</div>
                  </div>
                  {v.rating != null && <span className="text-[11px] text-amber-700 font-medium">★ {Number(v.rating).toFixed(1)}</span>}
                </Hit>
              ))}
            </Section>
          )}

          {hits.items.length > 0 && (
            <Section title="Products" icon={ShoppingBag}>
              {hits.items.map((it, i) => (
                <Hit key={`${it.vendor_id}-${i}`} data-testid={`hero-hit-item-${i}`}
                     onClick={() => { nav(`/vendor/${it.vendor_id}`); setOpen(false); }}>
                  {it.image
                    ? <img src={it.image} alt={it.name} className="h-9 w-9 rounded-lg object-cover" />
                    : <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center"><ShoppingBag className="h-4 w-4 text-slate-500" /></div>}
                  <div className="flex-1 min-w-0 truncate font-medium">{it.name}</div>
                  {it.price ? <span className="text-[12px] font-semibold text-slate-700">{formatMoney(it.price, it.currency)}</span> : null}
                </Hit>
              ))}
            </Section>
          )}

          {!empty && (
            <button onClick={handleSubmit} data-testid="hero-search-see-all"
              className="w-full px-4 py-3 text-sm font-medium text-emerald-700 hover:bg-emerald-50 flex items-center justify-center gap-1.5 border-t border-slate-100">
              <Sparkles className="h-3.5 w-3.5" /> See all results for "{q.trim()}"
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const Section = ({ title, icon: Icon, children }) => (
  <div>
    <div className="px-4 pt-3 pb-1 flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
      <Icon className="h-3 w-3" /> {title}
    </div>
    <div className="px-2 pb-2">{children}</div>
  </div>
);

const Hit = ({ onClick, children, ...rest }) => (
  <button
    onClick={onClick}
    {...rest}
    className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-slate-50 text-left text-sm"
  >
    {children}
  </button>
);

export default HeroSearch;
